import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DialogModal } from "../../../../components/common/dialog";
import { IContractDetailsResponseData } from "../../../../setup/api/services/contract/models";

import FeeBreakdownSection from "./FeeBreakdownSection";
import FeeTimeline from "./FeeTimeline";
import { getShippingFeeBreakdownState } from "./shippingFeeHelpers";
import modalStyles from "./deliveryFeeModal.module.scss";

interface Props {
  contract: IContractDetailsResponseData;
  open: boolean;
  onClose: () => void;
}

const DeliveryInfoModal = ({ contract, open, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const textDirection = i18n.dir();

  const feeBreakdown = contract?.shipment?.feeBreakdown;
  const breakdownState = useMemo(
    () => getShippingFeeBreakdownState(feeBreakdown),
    [feeBreakdown]
  );

  const shipmentCompany = useMemo(() => {
    if (contract?.shippable) {
      return (
        contract?.shipment?.shipmentCompany ??
        contract?.shipment?.provider ??
        t("lbl_more_contract_details_shipment_company")
      );
    }

    return contract?.shipment?.fee ||
      contract?.metaData?.sellerRequiresShippingInfo
      ? t("lbl_more_contract_details_shipment_company")
      : t("lbl_more_contract_details_no_shipment_exist");
  }, [contract, t]);

  const fallbackRows = useMemo(() => {
    const rows = [
      {
        label: t("lbl_more_details_sheet_shipping_by"),
        value: shipmentCompany,
      },
    ];

    if (contract?.deliveryInDays) {
      rows.push({
        label: t("lbl_more_details_sheet_delivery_duration"),
        value: t("lbl_contract_details_days_count", {
          count: contract.deliveryInDays,
        }),
      });
    }

    if (contract?.inspectionPeriod) {
      rows.push({
        label: t("lbl_more_details_checking_duration"),
        value: t("lbl_contract_details_days_count", {
          count: contract.inspectionPeriod,
        }),
      });
    }

    return rows;
  }, [
    contract?.deliveryInDays,
    contract?.inspectionPeriod,
    shipmentCompany,
    t,
  ]);

  const modalTitle = breakdownState
    ? t("lbl_shipping_fee_details_title")
    : t("lbl_contract_details_delivery_info");

  console.log("feeBreakdown:", feeBreakdown);
  return (
    <DialogModal open={open} handleClose={onClose} title={modalTitle}>
      <div>
        <div className={modalStyles.modalDivider} role="separator" />
        <div className={modalStyles.modalBody} dir={textDirection}>
          {breakdownState && feeBreakdown ? (
            <>
              <FeeTimeline state={breakdownState} feeBreakdown={feeBreakdown} />
              <FeeBreakdownSection
                state={breakdownState}
                settled={{
                  amount: feeBreakdown?.settled ?? 0,
                  actualShippingCost: feeBreakdown?.settledOriginalPrice ?? 0,
                  waffyProfit:
                    (feeBreakdown?.settled ?? 0) -
                    (feeBreakdown?.settledOriginalPrice ?? 0),
                }}
              />
            </>
          ) : (
            <>
              <p className={modalStyles.fallbackSubtitle}>
                {t("lbl_more_details_bottom_sheet_sub_title")}
              </p>
              <div className={modalStyles.fallbackRows}>
                {fallbackRows.map((row) => (
                  <div key={row.label} className={modalStyles.fallbackRow}>
                    <span className={modalStyles.fallbackLabel}>
                      {row.label}
                    </span>
                    <span className={modalStyles.fallbackValue}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </DialogModal>
  );
};

export default DeliveryInfoModal;
